import { preventDefault, stopEventPropagation } from "@tldraw/editor";
import { forwardRef, useEffect, useRef } from "react";

interface TextAreaProps {
  isEditing: boolean;
  text: string;
  handleFocus(): void;
  handleBlur(): void;
  handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void;
  handleChange(e: React.ChangeEvent<HTMLTextAreaElement>): void;
  handleInputPointerDown(e: React.PointerEvent<HTMLTextAreaElement>): void;
  handleDoubleClick(e: any): any;
  caretColor?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    isEditing,
    text,
    handleFocus,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleInputPointerDown,
    handleDoubleClick,
    caretColor,
  },
  ref
) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  // Combine refs
  useEffect(() => {
    if (typeof ref === "function") {
      ref(internalRef.current);
    } else if (ref) {
      (ref as any).current = internalRef.current;
    }
  }, [ref]);

  // Apply caret color with !important
  useEffect(() => {
    if (internalRef.current && caretColor) {
      internalRef.current.style.setProperty("caret-color", caretColor, "important");
    }
  }, [caretColor]);

  const handleKeyDownWrapper = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEditing) {
      e.stopPropagation();
      return;
    }
    handleKeyDown(e);
  };

  return (
    <textarea
      ref={internalRef}
      className="tl-text tl-text-input"
      name="text"
      tabIndex={-1}
      readOnly={!isEditing}
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      autoSave="off"
      placeholder=""
      spellCheck="true"
      wrap="off"
      dir="auto"
      defaultValue={text}
      onFocus={handleFocus}
      onChange={handleChange}
      onKeyDown={handleKeyDownWrapper}
      onBlur={handleBlur}
      onTouchEnd={stopEventPropagation}
      onContextMenu={isEditing ? stopEventPropagation : undefined}
      onPointerDown={handleInputPointerDown}
      onDoubleClick={handleDoubleClick}
      onDragStart={preventDefault}
    />
  );
});
